import { FileText } from "lucide-react";
import { useMemo } from "react";
import { DataTable, type DataTableColumn } from "../../../../components/table";
import type { DataTableSortDirection } from "../../../../components/table";
import { plainTextToHtml } from "../../../../lib/utils";
import { truncateText, truncateTitle } from "../../../../utils/string.utlis";
import { formatDateTime } from "../../../../utils/dateTime";
import type { NewsListItemResponse, NewsSortKey } from "../../types/news.types";

export interface NewsListDataTableProps {
  news: NewsListItemResponse[];
  loading: boolean;
  error?: string | null;
  page: number;
  totalPages: number;
  sortKey: NewsSortKey | null;
  sortDirection: DataTableSortDirection | null;
  onSort: (key: NewsSortKey, direction: DataTableSortDirection | null) => void;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  onRowClick: (newsItem: NewsListItemResponse) => void;
  thumbnailLabel: string;
  titleLabel: string;
  contentLabel: string;
  createdAtLabel: string;
  updatedAtLabel: string;
  loadingLabel?: React.ReactNode;
  emptyLabel?: React.ReactNode;
}

function NewsListDataTable({
  news,
  loading,
  error,
  page,
  totalPages,
  sortKey,
  sortDirection,
  onSort,
  onPageChange,
  onRetry,
  onRowClick,
  thumbnailLabel,
  titleLabel,
  contentLabel,
  createdAtLabel,
  updatedAtLabel,
  loadingLabel,
  emptyLabel = "Currently no news available.",
}: NewsListDataTableProps) {
  const columns = useMemo<DataTableColumn<NewsListItemResponse>[]>(
    () => [
      {
        key: "thumbnail",
        title: thumbnailLabel,
        render: (newsItem) =>
          newsItem.thumbnail_url ? (
            <img
              src={newsItem.thumbnail_url}
              alt={newsItem.title}
              className="h-12 w-12 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
              <FileText className="h-6 w-6 text-gray-400" />
            </div>
          ),
        width: 96,
      },
      {
        key: "title",
        title: titleLabel,
        sort: {
          direction: sortKey === "title" ? sortDirection : null,
          onSort: (nextDirection) => onSort("title", nextDirection),
          title: titleLabel,
        },
        minWidth: 350,
        render: (newsItem) => <div className="font-semibold text-base">{truncateTitle(newsItem.title)}</div>,
      },
      {
        key: "content",
        title: contentLabel,
        minWidth: 350,
        render: (newsItem) => (
          <div
            className="max-h-20 overflow-hidden text-sm text-muted-foreground"
            dangerouslySetInnerHTML={{
              __html: plainTextToHtml(truncateText(newsItem.content)),
            }}
          />
        ),
      },
      {
        key: "created_at",
        title: createdAtLabel,
        sort: {
          direction: sortKey === "created_at" ? sortDirection : null,
          onSort: (nextDirection) => onSort("created_at", nextDirection),
          title: createdAtLabel,
        },
        render: (newsItem) => formatDateTime(newsItem.created_at),
        width: 140,
      },
      {
        key: "updated_at",
        title: updatedAtLabel,
        sort: {
          direction: sortKey === "updated_at" ? sortDirection : null,
          onSort: (nextDirection) => onSort("updated_at", nextDirection),
          title: updatedAtLabel,
        },
        render: (newsItem) => formatDateTime(newsItem.updated_at),
        width: 140,
      },
    ],
    [
      contentLabel,
      createdAtLabel,
      onSort,
      sortDirection,
      sortKey,
      thumbnailLabel,
      titleLabel,
      updatedAtLabel,
    ],
  );

  return (
    <DataTable
      columns={columns}
      dataSource={news}
      rowKey="id"
      loading={loading}
      error={error}
      onRetry={onRetry}
      loadingLabel={loadingLabel}
      emptyLabel={emptyLabel}
      onRowClick={onRowClick}
      pagination={{
        page,
        totalPages,
        onPageChange,
      }}
      containerClassName="max-h-[calc(100vh-24rem)] overflow-auto"
    />
  );
}

export default NewsListDataTable;
